<template>
  <div class="container !pt-0">
    <el-form
        ref="integrationForm"
        :model="integrationForm"
        :rules="formRules"
        label-position="top"
        label-width="120px"
        @submit.native.prevent>

      <!-- Integration Name -->
      <el-row class="mb-6" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Integration Name" prop="name">
            <el-input
                v-model="integrationForm.name"
                class="gray"
                name="activecampaign-name"
                maxlength="255"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ActiveCampaign API Key -->
      <el-row class="mb-4" :gutter="20">
        <el-col :span="24">
          <el-form-item label="API Key" prop="activecampaign_integration.api_key">
            <el-input
                v-model="integrationForm.activecampaign_integration.api_key"
                class="gray"
                name="activecampaign-api-key"
                autocomplete="off"
                maxlength="255"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ActiveCampaign API URL -->
      <el-row class="mb-4" :gutter="20">
        <el-col :span="24">
          <el-form-item label="API URL" prop="activecampaign_integration.base_url">
            <el-input
                v-model="integrationForm.activecampaign_integration.base_url"
                class="gray"
                autocomplete="off"
                name="activecampaign-base-url"
                placeholder="https://youraccount.api-us1.com"
                maxlength="255"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <div v-if="integrationCreated">
        <!-- ActiveCampaign List Selection -->
        <el-row class="mb-2" :gutter="20">
          <el-col :span="24">
            <el-form-item label="ActiveCampaign List" prop="activecampaign_integration.list_id">
              <el-select
                  v-model="integrationForm.activecampaign_integration.list_id"
                  class="gray"
                  placeholder="Select a list"
                  :disabled="!credentialsProvided || activecampaignLists.length === 0"
                  @change="fetchActiveCampaignMergeFields">
                <el-option
                    v-for="list in activecampaignLists"
                    :key="list.id"
                    :label="list.name"
                    :value="list.id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Field Mapping -->
        <h3 class="mt-4 mb-2">Field Mapping</h3>
        <el-row class="mb-1" :gutter="20">
          <el-col :span="9">
            <el-form-item>
              <div slot="label" class="text-body-black">
                Internal Field
                <el-popover
                    placement="bottom"
                    trigger="hover"
                    content="This refers to the label names of the form elements used in your widget">
                  <strong slot="reference">
                    <i class="el-icon-info"></i>
                  </strong>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <i class="el-icon-right text-xl opacity-0"></i>
          </el-col>
          <el-col :span="1">
            <el-checkbox
                v-model="allFieldsActive"
                :disabled="!integrationForm.activecampaign_integration.list_id"
                @change="setAllFieldsActive(allFieldsActive)"></el-checkbox>
          </el-col>
          <el-col :span="11" :offset="1">
            <el-form-item>
              <div slot="label" class="text-body-black">
                External Field
                <el-popover
                    placement="bottom"
                    trigger="hover"
                    content="These are the field names that will be sent to ActiveCampaign. Note: Email is a mandatory field.">
                  <strong slot="reference">
                    <i class="el-icon-info"></i>
                  </strong>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <hr class="my-2"/>

        <el-row
            v-for="(mapping, index) in integrationForm.mapping"
            class="mb-2"
            :key="index"
            :gutter="20"
            type="flex"
            align="middle">
          <el-col :span="9">
            <span class="text-base">{{ mapping.internal.name }}</span>
          </el-col>
          <el-col :span="2">
            <i class="el-icon-right text-xl"></i>
          </el-col>
          <el-col :span="1">
            <el-checkbox
                v-model="mapping.active"
                :disabled="!integrationForm.activecampaign_integration.list_id"
                @change="checkAllFieldsActive"></el-checkbox>
          </el-col>
          <el-col :span="11" :offset="1">
            <el-form-item>
              <el-select
                  v-model="mapping.external.name"
                  class="gray"
                  :disabled="!mapping.active"
                  placeholder="Select ActiveCampaign Field">
                <el-option
                    key="email"
                    label="Email"
                    value="email"></el-option>
                <el-option
                    key="firstName"
                    label="First Name"
                    value="firstName"></el-option>
                <el-option
                    key="lastName"
                    label="Last Name"
                    value="lastName"></el-option>
                <el-option
                    key="phone"
                    label="Phone"
                    value="phone"></el-option>
                <el-option
                    v-for="field in activecampaignFields"
                    :key="field.value"
                    :label="field.label"
                    :value="field.value"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </div>
      <!-- Submit Button -->
      <div class="mt-8 flex justify-end">
        <button class="btn secondary outline big mr-4" @click="cancel">
          Cancel
        </button>
        <button class="btn primary big" @click="saveChanges">
          {{ integrationCreated ? 'Done' : 'Save' }}
        </button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import integrationAPI from '@api/integration'

export default {
  name: 'ActiveCampaign',
  data() {
    return {
      enableUpdate: false,
      integrationCreated: false,
      integrationForm: {
        name: '',
        mapping: [],
        target: 'ACTIVECAMPAIGN',
        activecampaign_integration: {
          api_key: '',
          base_url: '',
          list_id: '',
        },
      },
      formRules: {
        name: [
          { required: true, message: 'Integration name is required', trigger: 'blur' },
          { max: 255, message: 'Integration name must be less than 255 characters', trigger: 'blur' },
        ],
        'activecampaign_integration.api_key': [
          { required: true, message: 'API Key is required', trigger: 'blur' },
        ],
        'activecampaign_integration.base_url': [
          { required: true, message: 'API URL is required', trigger: 'blur' },
          { type: 'url', message: 'API URL must be a valid URL', trigger: 'blur' },
        ],
        'activecampaign_integration.list_id': [
          { required: true, message: 'ActiveCampaign List is required', trigger: 'blur' },
        ],
      },
      formModified: false,
      activecampaignLists: [],
      activecampaignFields: [],
      allFieldsActive: false,
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID']),
    ...mapGetters('campaign/widget', ['getWidgetFields']),
    integration() {
      return this.$store.getters['integration/targetIntegration']
    },
    credentialsProvided() {
      return (
          this.integrationForm.activecampaign_integration.api_key.trim() !== '' &&
          this.integrationForm.activecampaign_integration.base_url.trim() !== ''
      )
    },
  },
  watch: {
    integrationForm: {
      handler() {
        if (this.enableUpdate) {
          this.formModified = true
          this.$emit('form-modified', this.formModified)
        }
      },
      deep: true,
    },
    'integrationForm.activecampaign_integration.list_id': function (newVal) {
      if (newVal) {
        this.fetchActiveCampaignMergeFields()
      }
    },
  },
  created() {
    this.setFormValues(this.integration)
    if (this.targetIntegrationID) {
      this.integrationCreated = true
    }
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    saveChanges() {
      this.$refs.integrationForm.validate((valid) => {
        if (!valid) return

        // Only validate mapping after integration is created
        if (this.integrationCreated) {
          // Ensure email field is mapped
          const emailMapping = this.integrationForm.mapping.find(
              (mapping) => mapping.external.name === 'email' && mapping.active,
          )
          if (!emailMapping) {
            this.$message.error('The Email field must be mapped and active.')
            return
          }

          // Deactivate any mapping without external name
          this.integrationForm.mapping.forEach((mapping) => {
            if (!mapping.external.name || mapping.external.name === '') {
              mapping.active = false
            }
          })
        }

        const saveAction = this.targetIntegrationID
            ? 'integration/updateIntegration'
            : 'integration/createIntegration'

        this.$store
            .dispatch(saveAction, this.integrationForm)
            .then(() => {
              this.$message.success('Save successful!')

              if (!this.integrationCreated) {
                this.integrationCreated = true

                // Fetch lists now that integration is created
                this.fetchActiveCampaignLists()
              } else {
                this.$emit('force-close')
              }
            })
            .catch((error) => {
              this.$message.error('Failed to save integration.')
              console.error(error)
            })
      })
    },
    setFormValues(integration) {
      if (integration) {
        this.integrationForm.name = integration.name || ''
        this.integrationForm.activecampaign_integration.api_key = integration.activecampaign_integration.api_key || ''
        this.integrationForm.activecampaign_integration.base_url = integration.activecampaign_integration.base_url || ''
        this.integrationForm.activecampaign_integration.list_id = integration.activecampaign_integration.list_id || ''

        // Ensure mapping is an array
        this.integrationForm.mapping = Array.isArray(integration.mapping)
            ? JSON.parse(JSON.stringify(integration.mapping))
            : []

        this.fetchActiveCampaignLists().then(() => {
          if (this.integrationForm.activecampaign_integration.list_id) {
            this.fetchActiveCampaignMergeFields()
          }
        })
      }

      this.integrationForm.widget = this.$store.state.campaign.widget.targetWidgetID
      this.syncWidgetFieldsWithMapping()
    },
    syncWidgetFieldsWithMapping() {
      const widgetFields = this.getWidgetFields
      this.integrationForm.mapping = this.integrationForm.mapping
          .map(mappingItem => {
            const matchingField = widgetFields.find(field => field.id === mappingItem.internal.fieldID)
            if (matchingField) {
              mappingItem.internal.fieldID = matchingField.id
            }
            return mappingItem
          })
          .filter(mappingItem => widgetFields.some(field => field.id === mappingItem.internal.fieldID))

      widgetFields.forEach(field => {
        if (!this.integrationForm.mapping.some(mappingItem => mappingItem.internal.fieldID === field.id)) {
          this.integrationForm.mapping.push({
            internal: { name: field.settings.label, fieldID: field.id },
            external: { name: '' },
            active: false,
          })
        }
      })
      this.checkAllFieldsActive()
    },
    setAllFieldsActive(active) {
      this.integrationForm.mapping.forEach(item => {
        item.active = active
      })
    },
    checkAllFieldsActive() {
      this.allFieldsActive = this.integrationForm.mapping.every(item => item.active === true)
    },
    cancel() {
      if (this.formModified) {
        this.$emit('close-drawer')
      } else {
        this.$emit('force-close')
      }
    },
    fetchActiveCampaignLists() {
      return integrationAPI
          .fetchActiveCampaignLists(this.targetIntegrationID)
          .then((response) => {
            if (response.status !== 200) {
              throw new Error(`API Error: ${response.statusText}`)
            }
            const lists = response.data.lists
            if (!lists || !Array.isArray(lists)) {
              throw new Error('Invalid data format received from API')
            }
            this.activecampaignLists = lists.map((list) => ({
              id: list.id,
              name: list.name,
            }))
          })
          .catch((error) => {
            this.$message.error('Failed to fetch ActiveCampaign lists.')
            console.error('Error fetching lists:', error)
          })
    },
    fetchActiveCampaignMergeFields() {
      integrationAPI
          .fetchActiveCampaignMergeFields(
              this.targetIntegrationID,
              this.integrationForm.activecampaign_integration.list_id,
          )
          .then(response => {
            this.activecampaignFields = response.data.merge_fields.map(field => ({
              label: field.label,
              value: field.value,
            }))
          })
          .catch(error => {
            this.$message.error('Failed to fetch ActiveCampaign merge fields.')
            console.error(error)
          })
    },
  },
}
</script>